import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  RuleRecommendation,
  RuleRequest,
  addRule,
  deleteLabelingRule,
  getRuleList,
  getRuleListItem,
  getRuleRecommendationList,
  postDenyRuleRecommendation,
  simulateRule,
  updateRule,
  updateRuleEnable,
  updateRulePriority,
} from '~apis/rule';

import { TransactionsQueryKey } from '../transactions/query';
export const RuleQueryKey = {
  default: (companyId: string) => ['rule', companyId],
  detail: (ruleId: number | null) => ['rule', ruleId],
  simulate: (ruleId?: number) => ['rule', ruleId, 'simulate'],
  recommendation: (companyId: string) => ['rule', companyId, 'recommendation'],
};

export function useRuleRecommendationList(companyId: string) {
  return useQuery({
    queryKey: RuleQueryKey.recommendation(companyId),
    queryFn: () => getRuleRecommendationList(companyId),
    enabled: !!companyId,
  });
}

export function useRuleList(companyId: string) {
  return useQuery({
    queryKey: RuleQueryKey.default(companyId),
    queryFn: () => getRuleList(companyId),
    enabled: !!companyId,
  });
}

export function useAddRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; request: RuleRequest }) => addRule(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
      queryClient.invalidateQueries(TransactionsQueryKey.default());
    },
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      companyId: string;
      ruleId: number;
      request: RuleRequest & {
        isEnable: boolean;
      };
    }) => updateRule(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
      queryClient.invalidateQueries(TransactionsQueryKey.default());
    },
  });
}

export function useUpdateRuleEnable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; ruleId: number; isEnable: boolean }) =>
      updateRuleEnable(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; ruleId: number }) => deleteLabelingRule(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
    },
  });
}

export function useUpdatePriority() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; labelingRuleId: number; priority: number }) =>
      updateRulePriority(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(RuleQueryKey.default(variables.companyId));
    },
  });
}

export function useSimulateRule() {
  return useMutation({
    mutationFn: (data: {
      companyId: string;
      page: number;
      size: number;
      request: Omit<RuleRequest, 'ruleName' | 'overwrite'> & {
        ruleId: number | null;
      };
    }) =>
      simulateRule({
        companyId: data.companyId,
        request: data.request,
        page: data.page,
        size: data.size,
      }),
  });
}

export function useRuleListItem(companyId: string, ruleId: number | null) {
  return useQuery({
    queryKey: RuleQueryKey.detail(ruleId),
    queryFn: () => getRuleListItem(companyId, ruleId),
    enabled: !!companyId && !!ruleId,
  });
}

export function useDenyRuleRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { companyId: string; request: RuleRecommendation }) =>
      postDenyRuleRecommendation(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(RuleQueryKey.recommendation(variables.companyId));
    },
  });
}
